import { Modal, type ModalProps, Typography } from 'antd';
import type { ReactNode } from 'react';

interface FancyModalProps extends Omit<ModalProps, 'title'> {
  /** 主标题 */
  title: ReactNode;
  /** 副标题（标题下小字提示，如"带 * 为必填项"）*/
  subtitle?: ReactNode;
  /** 标题图标（来自 lucide-react）*/
  icon?: ReactNode;
  /** 多步表单进度（current 0-based, total）*/
  steps?: { current: number; total: number };
  /** body 区域最大高度（默认 70vh，超出滚动）*/
  bodyMaxHeight?: number | string;
  /** 是否在 body 与 footer 间显示分隔线（默认 true）*/
  footerDivider?: boolean;
}

/**
 * 美化版 Modal (M37, Tier 3)
 *
 * 与原 AntD Modal 区别:
 *   - 大圆角 16px (theme 已配，这里再补保险)
 *   - 标题区独立 padding，主标题 18px + 副标题 12px
 *   - 标题左侧可选 icon (品牌色软背景)
 *   - body 自带 maxHeight + 滚动 (避免内容多顶到底部)
 *   - footer 区可选分隔线 + 浅背景
 *   - 多步表单可显示底部进度小圆点
 *
 * 用法:
 *   <FancyModal
 *     open={open}
 *     onCancel={...}
 *     onOk={...}
 *     title="新建客户"
 *     subtitle="带 * 为必填项"
 *     icon={<Building2 size={18} />}
 *     steps={{ current: 0, total: 3 }}
 *   >
 *     <Form>...</Form>
 *   </FancyModal>
 */
export function FancyModal({
  title,
  subtitle,
  icon,
  steps,
  bodyMaxHeight = '70vh',
  footerDivider = true,
  children,
  width = 560,
  ...rest
}: FancyModalProps) {
  return (
    <Modal
      {...rest}
      centered
      width={width}
      title={null}
      styles={{
        ...rest.styles,
        content: {
          padding: 0,
          overflow: 'hidden',
          borderRadius: 16,
          ...(rest.styles?.content ?? {}),
        },
        body: { padding: 0, ...(rest.styles?.body ?? {}) },
        footer: { margin: 0, padding: '12px 24px 20px', ...(rest.styles?.footer ?? {}) },
      }}
      classNames={{
        ...(rest.classNames ?? {}),
      }}
    >
      {/* ── Header ───────────────────────────── */}
      <div
        style={{
          padding: '18px 24px 16px',
          borderBottom: '1px solid #f0f1f3',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        {icon && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(0, 120, 212, 0.10)',
              color: '#0078d4',
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            {icon}
          </span>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <Typography.Title level={4} style={{ margin: 0, fontSize: 17, lineHeight: 1.4 }}>
            {title}
          </Typography.Title>
          {subtitle && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 4, display: 'block' }}
            >
              {subtitle}
            </Typography.Text>
          )}
        </div>
      </div>

      {/* ── Body ─────────────────────────────── */}
      <div
        style={{
          padding: '20px 24px',
          maxHeight: bodyMaxHeight,
          overflowY: 'auto',
        }}
      >
        {children}
      </div>

      {/* ── Step indicator (optional) ────────── */}
      {steps && (
        <div
          style={{
            padding: '10px 24px',
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
            background: '#fafbfc',
            borderTop: footerDivider ? '1px solid #f0f1f3' : 'none',
          }}
        >
          {Array.from({ length: steps.total }).map((_, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: step dots are stable
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                background: i <= steps.current ? '#0078d4' : '#d1d5db',
                transition: 'background 0.2s ease',
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}
